import { analytics } from 'affiny-core/analytics';
import { Button } from 'affiny-ui/src';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import rem from '../../utils/rem';
import { MessageInput } from '../message-input';
import { Box, Flex, Text, Title } from '../primitives';
import { BREAKER_TYPE, Props } from './breaker.types';
import BreakerIllustration from './components/breaker-illustration';

const Breaker = ({
  title,
  subtitle,
  textBtn,
  type,
  illustration,
  specialOfferText,
  boxStyle,
  trackEvent,
  onClick,
  ...style
}: Props) => {
  const { t } = useTranslation();

  const handleClick = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onClick) {
      onClick();
    }

    if (trackEvent) {
      analytics.trackEvent(trackEvent);
    }

    return false;
  }, []);

  const isThreadBreaker =
    type === BREAKER_TYPE.FTA ||
    type === BREAKER_TYPE.FTA_RECEIVED ||
    type === BREAKER_TYPE.FTI_SENT;
  const rootStyle = isThreadBreaker
    ? {
        overflow: 'auto',
      }
    : {
        alignItems: 'center',
        justifyContent: 'center',
      };
  const titleStyleWrapperStyle = isThreadBreaker ? {} : { flex: 'none' };
  const subTitleWrapperStyle = isThreadBreaker
    ? {
        flex: 'none',
        boxShadow: 'box',
        borderRadius: 'small',
        alignItems: 'center',
        width: rem(483),
        mb: rem(24),
        px: rem(37),
        pt: rem(24),
        pb: rem(26),
      }
    : {};

  const renderSubtitleAndButton = () => {
    const result = (
      <>
        {subtitle && (
          <Text
            variant="medium"
            textAlign="center"
            color={boxStyle ? 'caribbeanGreen' : Text.defaultProps?.color}
            mt="small"
            {...style.subTitleStyle}
          >
            {subtitle}
          </Text>
        )}

        {specialOfferText && (
          <Text
            textAlign="center"
            variant="large"
            mt={rem(8)}
            fontWeight={600}
            color="caribbeanGreen"
          >
            {specialOfferText}
          </Text>
        )}
        <Box mt="1rem">
          <Button
            color="subscription"
            variant="contained"
            size="small"
            onClick={handleClick}
            text={
              textBtn
                ? textBtn
                : t(
                    isThreadBreaker
                      ? 'ratecards.threadAction'
                      : 'ratecards.action'
                  )
            }
          />
        </Box>
      </>
    );

    return isThreadBreaker ? (
      <Flex flexDirection="column" {...subTitleWrapperStyle}>
        {result}
      </Flex>
    ) : (
      result
    );
  };

  return (
    <SContainer
      flexDirection="column"
      height="100%"
      cursor="pointer"
      onClick={handleClick}
      type={type}
      {...(type === BREAKER_TYPE.LIST && {
        bg: 'light',
        boxShadow: 'box',
      })}
      {...rootStyle}
      {...boxStyle}
      data-test="goToRateCard-from-Breaker"
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        {...style.wrapperStyle}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          {...titleStyleWrapperStyle}
        >
          {type !== BREAKER_TYPE.LIST &&
            type !== BREAKER_TYPE.PHOTO &&
            !(type === BREAKER_TYPE.FTA_RECEIVED && !illustration) && (
              <BreakerIllustration type={type} illustration={illustration} />
            )}

          <Title
            fontSize={rem(18)}
            lineHeight={rem(24)}
            textAlign="center"
            {...style.titleStyle}
          >
            {title}
          </Title>
        </Flex>

        {renderSubtitleAndButton()}
      </Flex>
      {isThreadBreaker && (
        <SBox>
          <MessageInput color="caribbeanGreen" />
        </SBox>
      )}
    </SContainer>
  );
};

const SContainer = styled(Flex)<{ type: BREAKER_TYPE }>`
  ${({ type }) =>
    type === BREAKER_TYPE.LIST &&
    `
      position: sticky;
      left: 0;
      bottom: 0;
    `}
`;

const SBox = styled(Box)`
  pointer-events: none;
`;

export default memo(Breaker);
